/**
 * Speech Recognition Service
 * Provides a reusable Web Speech API wrapper for voice-to-text functionality
 */

import { user_data } from 'src/stores/account/user';
import { app } from 'src/stores/session/native-bridge';
import { get } from 'svelte/store';

export interface SpeechRecognitionOptions {
	language?: string;
	continuous?: boolean;
	interimResults?: boolean;
	onStart?: () => void;
	onResult?: (transcript: string, isFinal: boolean) => void;
	onError?: (error: SpeechRecognitionError) => void;
	onEnd?: () => void;
}

export interface SpeechRecognitionError {
	type: 'not-supported' | 'not-allowed' | 'no-speech' | 'network' | 'aborted' | 'unknown';
	message: string;
	translationKey: string;
}

export interface SpeechRecognitionController {
	start: () => void;
	stop: () => void;
	isSupported: () => boolean;
	isRecording: () => boolean;
}

// Type declarations for Web Speech API (not fully typed in TypeScript)
interface SpeechRecognitionEvent extends Event {
	results: SpeechRecognitionResultList;
	resultIndex: number;
}

interface SpeechRecognitionResultList {
	length: number;
	item(index: number): SpeechRecognitionResult;
	[index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
	length: number;
	item(index: number): SpeechRecognitionAlternative;
	[index: number]: SpeechRecognitionAlternative;
	isFinal: boolean;
}

interface SpeechRecognitionAlternative {
	transcript: string;
	confidence: number;
}

interface SpeechRecognitionErrorEvent extends Event {
	error: string;
	message: string;
}

declare global {
	interface Window {
		SpeechRecognition: new () => SpeechRecognition;
		webkitSpeechRecognition: new () => SpeechRecognition;
	}

	interface SpeechRecognition extends EventTarget {
		continuous: boolean;
		interimResults: boolean;
		lang: string;
		maxAlternatives: number;
		onstart: ((this: SpeechRecognition, ev: Event) => void) | null;
		onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => void) | null;
		onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => void) | null;
		onend: ((this: SpeechRecognition, ev: Event) => void) | null;
		start(): void;
		stop(): void;
		abort(): void;
	}
}

/**
 * Check if the browser supports Web Speech API
 */
export function isSpeechRecognitionSupported(): boolean {
	if (get(app)) return false;
	return !!(window.SpeechRecognition && window.webkitSpeechRecognition);
}

/**
 * Check if the current page is served over HTTPS (required for speech recognition)
 */
export function isSecureContext(): boolean {
	return (
		window.location.protocol === 'https:' ||
		window.location.hostname === 'localhost' ||
		window.location.hostname === '127.0.0.1'
	);
}

/**
 * Get the browser's preferred language for speech recognition
 */
export function getBrowserLanguage(): string {
	return get(user_data).lang || navigator.language || 'en-US';
}

/**
 * Create a speech recognition controller
 */
export function createSpeechRecognition(
	options: SpeechRecognitionOptions = {},
): SpeechRecognitionController {
	let recognition: SpeechRecognition | null = null;
	let isActive = false;

	const {
		language = getBrowserLanguage(),
		continuous = false,
		interimResults = true,
		onStart,
		onResult,
		onError,
		onEnd,
	} = options;

	function mapErrorType(errorCode: string): SpeechRecognitionError['type'] {
		switch (errorCode) {
			case 'not-allowed':
			case 'service-not-allowed':
				return 'not-allowed';
			case 'no-speech':
				return 'no-speech';
			case 'network':
				return 'network';
			case 'aborted':
				return 'aborted';
			default:
				return 'unknown';
		}
	}

	function getErrorInfo(errorType: SpeechRecognitionError['type']): {
		message: string;
		translationKey: string;
	} {
		switch (errorType) {
			case 'not-supported':
				return {
					message: 'Voice input is not supported in this browser',
					translationKey: 'ai_chat_voice_not_supported',
				};
			case 'not-allowed':
				return {
					message: 'Microphone access denied. Please allow microphone access for voice input',
					translationKey: 'ai_chat_microphone_denied',
				};
			case 'no-speech':
				return {
					message: 'No speech detected. Please try again',
					translationKey: 'ai_chat_no_speech',
				};
			case 'network':
				return {
					message: 'Network error occurred. Please check your connection',
					translationKey: 'ai_chat_voice_error',
				};
			case 'aborted':
				return {
					message: 'Voice input was cancelled',
					translationKey: 'ai_chat_voice_error',
				};
			default:
				return {
					message: 'An error occurred with voice input',
					translationKey: 'ai_chat_voice_error',
				};
		}
	}

	function initRecognition(): boolean {
		console.log('[SpeechAPI] Initializing recognition...');

		if (!isSpeechRecognitionSupported()) {
			console.error('[SpeechAPI] Not supported');
			const errorInfo = getErrorInfo('not-supported');
			onError?.({
				type: 'not-supported',
				...errorInfo,
			});
			return false;
		}

		if (!isSecureContext()) {
			console.error('[SpeechAPI] Not in secure context');
			onError?.({
				type: 'not-allowed',
				message: 'Voice input requires a secure connection (HTTPS)',
				translationKey: 'ai_chat_microphone_denied',
			});
			return false;
		}

		console.log('[SpeechAPI] Creating recognition instance...');
		const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
		recognition = new SpeechRecognitionAPI();
		console.log('[SpeechAPI] Recognition instance created');

		recognition.continuous = continuous;
		recognition.interimResults = interimResults;
		recognition.lang = language;
		recognition.maxAlternatives = 1;
		console.log('[SpeechAPI] Config:', { continuous, interimResults, language });

		recognition.onstart = () => {
			console.log('[SpeechAPI] onstart fired');
			isActive = true;
			onStart?.();
		};

		recognition.onresult = (event: SpeechRecognitionEvent) => {
			console.log('[SpeechAPI] onresult fired, results:', event.results.length);
			let finalTranscript = '';
			let interimTranscript = '';

			for (let i = event.resultIndex; i < event.results.length; i++) {
				const result = event.results[i];
				const transcript = result[0].transcript;

				if (result.isFinal) {
					finalTranscript += transcript;
				} else {
					interimTranscript += transcript;
				}
			}

			if (finalTranscript) {
				onResult?.(finalTranscript, true);
			} else if (interimTranscript) {
				onResult?.(interimTranscript, false);
			}
		};

		recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
			console.error('[SpeechAPI] onerror fired:', event.error, event.message);
			const errorType = mapErrorType(event.error);
			const errorInfo = getErrorInfo(errorType);
			onError?.({
				type: errorType,
				...errorInfo,
			});
		};

		recognition.onend = () => {
			console.log('[SpeechAPI] onend fired');
			isActive = false;
			onEnd?.();
		};

		return true;
	}

	return {
		start() {
			console.log('[SpeechAPI] start() called, isActive:', isActive);
			if (isActive) {
				console.log('[SpeechAPI] Already active, returning');
				return;
			}

			if (!recognition) {
				console.log('[SpeechAPI] No recognition instance, initializing...');
				if (!initRecognition()) {
					console.error('[SpeechAPI] Initialization failed');
					return;
				}
			}

			try {
				console.log('[SpeechAPI] Calling recognition.start()...');
				recognition?.start();
				console.log('[SpeechAPI] recognition.start() completed');
			} catch (error) {
				console.error('[SpeechAPI] Exception in start():', error);
				// Handle case where recognition is already started
				console.warn('Speech recognition already started or error:', error);
			}
		},

		stop() {
			if (!isActive || !recognition) return;

			try {
				recognition.stop();
			} catch (error) {
				console.warn('Error stopping speech recognition:', error);
			}
		},

		isSupported() {
			return isSpeechRecognitionSupported() && isSecureContext();
		},

		isRecording() {
			return isActive;
		},
	};
}
